/**
 * Created by katarinababic on 25.11.24.
 */
import * as React from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../Theme/Theme';
import { Translation } from '../../Components/Translation';
import { ExploreCategorySwitch } from '../../Components/ExploreCards/ExploreCategorySwitch';
import { useExploreCategoriesSuspense } from '../../Hooks/useExploreCategoriesSuspense';
import { InitialCategoriesSkeleton } from '../../Components/Skeletons/InitialCategoriesSkeleton';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  padding-bottom: 48px;
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, ${Theme.Colors.secondary}, ${Theme.Colors.secondaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  background: #fafafa;
  padding: 32px 0 24px;
`;

export type MentalHealthLibraryScreenProps = {};

// Level-by-level browse: V3 has no flat subtree query, so the "library" now
// shows the Mental root's child categories (each renders its own preview row).
const MentalHealthLibraryCategories: React.FC = observer(() => {
  const { categories } = useExploreCategoriesSuspense('mental');

  return (
    <>
      {categories.map((category, index) => (
        <ExploreCategorySwitch
          key={category.id}
          category={category}
          categoryIndex={index}
          isLast={index === categories.length - 1}
          linkPrefix="/mental-health/"
        />
      ))}
    </>
  );
});

export const MentalHealthLibraryScreen: React.FC<MentalHealthLibraryScreenProps> = observer(() => {
  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <Translation i18nKey="mentalHealthLibrary.title" defaultValue="Mental Health Library" />
          </HeroTitle>
          <HeroSubtitle>
            <Translation
              i18nKey="mentalHealthLibrary.description"
              defaultValue="Missed a live session? No worries. You can find a selection of all mental health contents in our on demand library."
            />
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      <CategoriesSection>
        <Suspense fallback={<InitialCategoriesSkeleton />}>
          <MentalHealthLibraryCategories />
        </Suspense>
      </CategoriesSection>
    </ScreenContainer>
  );
});
